import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Download, FileText, Loader2, RefreshCw, Trash2 } from "lucide-react";

const formatDetail = (d) => {
  if (d == null) return "Errore di rete, riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
};

/* ---------- SINISTRI ---------- */
export const SinistriTab = ({ token, api, viewDoc }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => { api(token).get("/incidents").then(({ data }) => setItems(data.incidents)).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const setStatus = async (id, status) => {
    try { await api(token).patch(`/incidents/${id}`, { status }); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const download = async (id) => {
    setBusy(id);
    try {
      const r = await api(token).get(`/incidents/${id}/package`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `fascicolo_${id}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Fascicolo scaricato (ZIP con PDF riepilogativo e foto)");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const badge = (s) => (s === "chiusa" ? "bg-green-100 text-green-800" : s === "in_lavorazione" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-fr-red");
  return (
    <div data-testid="admin-tab-sinistri">
      <p className="text-xs text-gray-500 mb-3">Pratiche sinistro aperte dai clienti. Fascicolo scaricabile (ZIP: riepilogo PDF + foto/documenti) da inviare ad avvocato e assicurazione [validazione legale: DA PROFESSIONISTA].</p>
      <button onClick={load} className="mb-3 h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 flex items-center gap-1 hover:bg-gray-200"><RefreshCw size={12} /> Aggiorna</button>
      {items.length === 0 && <p className="text-sm text-gray-400">Nessuna pratica.</p>}
      <div className="space-y-2" data-testid="admin-incidents-list">
        {items.map((i) => (
          <div key={i.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={`admin-incident-${i.reference}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-extrabold text-fr-navy text-sm">{i.reference}</span>
              <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${badge(i.status)}`}>{i.status.toUpperCase()}</span>
              <span className="text-[11px] text-gray-400">{format(new Date(i.created_at), "d MMM yyyy HH:mm", { locale: it })}</span>
              <button onClick={() => download(i.id)} className="ml-auto h-8 rounded-lg bg-fr-navy text-white text-[11px] font-bold px-3 flex items-center gap-1 hover:bg-fr-navydark" data-testid={`admin-incident-download-${i.reference}`}>
                {busy === i.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Fascicolo ZIP
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-1">Cliente: {i.account_email} • Prenotazione: {i.booking_reference || "n/d"} • Targa: {i.targa || "n/d"}</p>
            {i.gps?.lat && <p className="text-[11px] text-gray-400 mt-0.5">GPS: <a className="underline text-fr-navy" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${i.gps.lat},${i.gps.lng}`}>{i.gps.lat.toFixed(5)}, {i.gps.lng.toFixed(5)}</a></p>}
            <p className="text-xs text-gray-700 mt-1.5 bg-gray-50 rounded-lg p-2" data-testid={`admin-incident-facts-${i.reference}`}>{i.facts}</p>
            {i.counterpart?.counterpart_name && <p className="text-[11px] text-gray-500 mt-1">Controparte: {i.counterpart.counterpart_name} • targa {i.counterpart.counterpart_plate} • {i.counterpart.counterpart_insurer} • {i.counterpart.counterpart_phone}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {i.files.map((f, idx) => (
                <button key={idx} onClick={() => viewDoc(f.path)} className="text-[10px] font-bold bg-gray-100 rounded px-2 py-1 hover:bg-gray-200" data-testid={`admin-incident-file-${idx}`}>
                  <FileText size={10} className="inline mr-1" />{f.step}
                </button>
              ))}
              {["in_lavorazione", "chiusa"].map((s) => (
                <button key={s} onClick={() => setStatus(i.id, s)} className="ml-auto text-[10px] font-bold bg-gray-100 rounded px-2 py-1 hover:bg-gray-200" data-testid={`admin-incident-status-${s}`}>→ {s}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- PAGAMENTI ---------- */
export const PagamentiTab = ({ token, api, secure }) => {
  const [items, setItems] = useState([]);
  const [manual, setManual] = useState({ booking_reference: "", amount: "", kind: "cauzione_contanti" });
  const [recon, setRecon] = useState(null);
  const load = () => { api(token).get("/payments").then(({ data }) => setItems(data.payments)).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const op = async (id, action, body = {}) => {
    const sens = ["capture", "release", "refund"].includes(action);
    const run = (h) => api(token).post(`/payments/${id}/${action}`, body, { headers: h });
    try {
      if (sens && secure) await secure(run);
      else await run({});
      toast.success("Operazione registrata"); load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const reconcile = async () => {
    try {
      const fn = (h) => api(token).post("/payments/reconcile", {}, { headers: h });
      const { data } = await (secure ? secure(fn) : fn({}));
      setRecon(data.differences || []);
      toast.success(data.differences?.length ? `${data.differences.length} differenze da verificare` : "Nessuna differenza");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const addManual = async () => {
    try {
      await api(token).post("/payments/manual", { booking_reference: manual.booking_reference, amount: manual.amount ? Number(manual.amount) : null, kind: manual.kind });
      toast.success("Cauzione alternativa registrata");
      setManual({ booking_reference: "", amount: "", kind: "cauzione_contanti" });
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const badge = (s) => ({ incassato: "bg-green-100 text-green-800", autorizzato: "bg-blue-100 text-blue-800", rimborsato: "bg-gray-100 text-gray-600", annullato: "bg-gray-100 text-gray-600", cauzione_rilasciata: "bg-amber-100 text-amber-800", fallito: "bg-red-100 text-fr-red", in_attesa: "bg-amber-100 text-amber-800", da_verificare: "bg-purple-100 text-purple-800" }[s] || "bg-gray-100 text-gray-600");
  return (
    <div data-testid="admin-tab-pagamenti">
      <p className="text-xs text-gray-500 mb-3">Stati separati: una <b>preautorizzazione</b> NON è un incasso; una cauzione autorizzata non è denaro dell'azienda. Operazioni consentite solo a titolare/amministratore con <b>riautenticazione recente</b> (A3). Nessun dato carta è salvato nel gestionale (PCI-DSS).</p>
      <button onClick={reconcile} className="mb-3 h-8 rounded-lg border border-fr-navy text-fr-navy text-[11px] font-bold px-3" data-testid="admin-payments-reconcile">Riconcilia con Stripe (segnala differenze)</button>
      {recon?.length > 0 && (
        <div className="mb-3 bg-purple-50 border border-purple-200 rounded-xl p-3 text-[11px] text-purple-900" data-testid="admin-payments-reconcile-result">
          <b>Esiti incerti (verifica a mano, nessuna correzione automatica):</b>
          {recon.map((d, i) => <p key={i}>{d.booking_reference}: locale={d.local} → stripe={d.stripe}</p>)}
        </div>
      )}
      <div className="mb-4 bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap gap-2 items-end" data-testid="admin-payments-manual">
        <label className="text-[10px] font-bold text-gray-400 uppercase">Cauzione alternativa (contanti/bonifico) — no carta
          <input value={manual.booking_reference} onChange={(e) => setManual({ ...manual, booking_reference: e.target.value })} placeholder="FR-XXXXXX" className="block h-9 w-32 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-manual-ref" />
        </label>
        <label className="text-[10px] font-bold text-gray-400 uppercase">Importo €
          <input type="number" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} className="block h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-manual-amount" />
        </label>
        <select value={manual.kind} onChange={(e) => setManual({ ...manual, kind: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-manual-kind">
          <option value="cauzione_contanti">Cauzione contanti</option>
          <option value="cauzione_bonifico">Cauzione bonifico</option>
          <option value="anticipo">Anticipo/pagamento completo</option>
        </select>
        <button onClick={addManual} className="h-9 rounded-lg bg-fr-navy text-white text-[11px] font-bold px-3" data-testid="admin-manual-add">Registra</button>
      </div>
      <div className="space-y-2" data-testid="admin-payments-list">
        {items.length === 0 && <p className="text-sm text-gray-400">Nessun pagamento.</p>}
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={`admin-payment-${p.booking_reference}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-extrabold text-fr-navy text-sm">{p.booking_reference}</span>
              <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${badge(p.status)}`}>{p.status.toUpperCase()}</span>
              <span className="text-[11px] text-gray-500">{p.kind} — cliente €{p.amount_total} (di cui cauzione €{p.deposit_amount})</span>
              {p.partner_email && <span className="text-[10px] text-fr-red font-bold">via {p.partner_email}</span>}
              {(p.review_reason || p.failure_reason) && <span className="text-[10px] text-purple-800 font-bold">⚠ {p.review_reason || p.failure_reason}</span>}
              {p.card_funding && <span className="text-[10px] text-gray-400">carta: {p.card_funding}</span>}
              {p.name_match === false && <span className="text-[10px] text-amber-700 font-bold">titolare carta ≠ conducente: da verificare</span>}
            </div>
            <div className="mt-2 flex gap-2">
              {p.status === "autorizzato" && <button onClick={() => op(p.id, "capture")} className="h-8 rounded-lg bg-green-600 text-white text-[11px] font-bold px-3" data-testid={`admin-payment-capture-${p.booking_reference}`}>Incassa</button>}
              {["in_attesa", "autorizzato"].includes(p.status) && <button onClick={() => op(p.id, "release")} className="h-8 rounded-lg bg-amber-100 text-amber-800 text-[11px] font-bold px-3" data-testid={`admin-payment-release-${p.booking_reference}`}>Rilascia cauzione</button>}
              {p.status === "da_verificare" && <button onClick={() => op(p.id, "confirm-review")} className="h-8 rounded-lg bg-purple-600 text-white text-[11px] font-bold px-3">Ho verificato carta+documento → autorizzato</button>}
              {p.status === "incassato" && <button onClick={() => op(p.id, "refund")} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3" data-testid={`admin-payment-refund-${p.booking_reference}`}>Rimborsa</button>}
              {p.kind.startsWith("cauzione_") && p.status === "in_attesa" && <button onClick={() => op(p.id, "manual-settle")} className="h-8 rounded-lg bg-green-600 text-white text-[11px] font-bold px-3">Segna incassata</button>}
              {p.kind.startsWith("cauzione_") && ["incassato"].includes(p.status) && <button onClick={() => op(p.id, "manual-return")} className="h-8 rounded-lg bg-amber-100 text-amber-800 text-[11px] font-bold px-3">Segna restituita</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- UTENTI ---------- */
export const UtentiTab = ({ token, api, me, secure }) => {
  const [users, setUsers] = useState([]);
  const [nw, setNw] = useState({ email: "", password: "", role: "operatore", first_name: "" });
  const load = () => { api(token).get("/users").then(({ data }) => setUsers(data.users)).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const create = async () => {
    const run = (h) => api(token).post("/users", nw, { headers: h });
    try { await (secure ? secure(run) : run({})); toast.success("Utente creato"); setNw({ email: "", password: "", role: "operatore", first_name: "" }); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const patch = async (id, body) => {
    const run = (h) => api(token).patch(`/users/${id}`, body, { headers: h });
    try { await (secure ? secure(run) : run({})); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    const run = (h) => api(token).delete(`/users/${id}`, { headers: h });
    try { await (secure ? secure(run) : run({})); toast.success("Utente eliminato"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="admin-tab-utenti">
      <p className="text-xs text-gray-500 mb-3">Gestione utenti riservata al titolare. Ruoli: titolare (tutto), amministratore (tutto tranne utenti/chiavi), operatore (prenotazioni, clienti, consegne, verbali, sinistri, multe — niente prezzi/pagamenti/config), driver (solo consegne/ritiri assegnati). MFA attivabile dall'utente nella tab Password.</p>
      <div className="flex flex-wrap gap-2 bg-white border border-gray-200 rounded-xl p-3 mb-4 items-end" data-testid="admin-users-new">
        <input value={nw.first_name} onChange={(e) => setNw({ ...nw, first_name: e.target.value })} placeholder="Nome" className="h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-user-new-name" />
        <input value={nw.email} onChange={(e) => setNw({ ...nw, email: e.target.value })} placeholder="email@fabrirent.it" className="h-9 w-48 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-user-new-email" />
        <input value={nw.password} onChange={(e) => setNw({ ...nw, password: e.target.value })} placeholder="Password (min 12)" type="password" className="h-9 w-36 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-user-new-password" />
        <select value={nw.role} onChange={(e) => setNw({ ...nw, role: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-user-new-role">
          <option value="operatore">Operatore</option>
          <option value="amministratore">Amministratore</option>
          <option value="driver">Driver</option>
          <option value="titolare">Titolare</option>
        </select>
        <button onClick={create} className="h-9 rounded-lg bg-fr-navy text-white text-xs font-bold px-3" data-testid="admin-user-create">Crea utente</button>
      </div>
      <div className="space-y-1.5" data-testid="admin-users-list">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2" data-testid={`admin-user-${u.email}`}>
            <span className="text-xs font-bold text-fr-navy">{u.first_name || "—"} {u.email}</span>
            <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${u.role === "titolare" ? "bg-fr-ink text-white" : "bg-gray-100 text-gray-600"}`}>{u.role}</span>
            {u.totp_enabled && <span className="text-[10px] font-bold text-green-700">MFA ON</span>}
            {u.active === false && <span className="text-[10px] font-bold text-fr-red">DISATTIVATO</span>}
            <div className="ml-auto flex gap-1.5">
              <button onClick={() => patch(u.id, { active: u.active === false })} className="h-7 rounded-lg bg-gray-100 text-[10px] font-bold px-2">{u.active === false ? "Riattiva" : "Disattiva"}</button>
              {u.email !== me?.email && <button onClick={() => remove(u.id)} className="h-7 rounded-lg bg-red-50 text-fr-red text-[10px] font-bold px-2"><Trash2 size={11} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- IMPOSTAZIONI ---------- */
export const ImpostazioniTab = ({ token, api, secure }) => {
  const [g, setG] = useState({ roadside_phone: "", deposit_amount: 300, driver_buffer_minutes: 90, modify_proposal_hours: 24 });
  const [outbox, setOutbox] = useState([]);
  const [audit, setAudit] = useState([]);
  const [content, setContent] = useState({});
  const load = () => {
    api(token).get("/settings/general").then(({ data }) => setG({ roadside_phone: data.roadside_phone, deposit_amount: data.deposit_amount, driver_buffer_minutes: data.driver_buffer_minutes || 90, modify_proposal_hours: data.modify_proposal_hours || 24 })).catch(() => {});
    api(token).get("/outbox").then(({ data }) => setOutbox(data.outbox)).catch(() => {});
    api(token).get("/audit-log").then(({ data }) => setAudit(data.log)).catch(() => {});
    api(token).get("/content").then(({ data }) => setContent(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [token]);

  const saveContent = async () => {
    try {
      await api(token).post("/content", { rental_terms_it: content.rental_terms_it || "", rental_terms_en: content.rental_terms_en || "", privacy_it: content.privacy_it || "", privacy_en: content.privacy_en || "" });
      toast.success("Contenuti legali salvati");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    const run = (h) => api(token).post("/settings/general", { roadside_phone: g.roadside_phone, deposit_amount: Number(g.deposit_amount), driver_buffer_minutes: Number(g.driver_buffer_minutes), modify_proposal_hours: Number(g.modify_proposal_hours) }, { headers: h });
    try { await (secure ? secure(run) : run({})); toast.success("Impostazioni salvate"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admin-tab-impostazioni">
      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="admin-settings-general">
        <p className="text-sm font-extrabold text-fr-navy">Impostazioni generali</p>
        <label className="block mt-2 text-[10px] font-bold text-gray-400 uppercase">Numero soccorso stradale (verificare che corrisponda alla polizza del veicolo)
          <input value={g.roadside_phone} onChange={(e) => setG({ ...g, roadside_phone: e.target.value })} className="mt-1 h-9 w-44 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm" data-testid="admin-roadside-phone" />
        </label>
        <label className="block mt-2 text-[10px] font-bold text-gray-400 uppercase">Deposito cauzionale (€, per preautorizzazione Stripe)
          <input type="number" value={g.deposit_amount} onChange={(e) => setG({ ...g, deposit_amount: e.target.value })} className="mt-1 h-9 w-32 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm" data-testid="admin-deposit-amount" />
        </label>
        <label className="block mt-2 text-[10px] font-bold text-gray-400 uppercase">Buffer sovrapposizione driver (minuti, B3)
          <input type="number" value={g.driver_buffer_minutes} onChange={(e) => setG({ ...g, driver_buffer_minutes: e.target.value })} className="mt-1 h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm" data-testid="admin-driver-buffer" />
        </label>
        <label className="block mt-2 text-[10px] font-bold text-gray-400 uppercase">Scadenza proposte di modifica prenotazione (ore, C5)
          <input type="number" value={g.modify_proposal_hours} onChange={(e) => setG({ ...g, modify_proposal_hours: e.target.value })} className="mt-1 h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-sm" data-testid="admin-proposal-hours" />
        </label>
        <button onClick={save} className="mt-3 h-9 rounded-lg bg-fr-navy text-white text-xs font-bold px-4" data-testid="admin-settings-save">Salva</button>
      </div>
      {/* COMANDO SITO 5.3: contenuti legali modificabili dal titolare (IT/EN) */}
      <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4" data-testid="admin-content-editor">
        <p className="text-sm font-extrabold text-fr-navy">Condizioni di noleggio e Privacy (testo validato dall'avvocato — IT/EN)</p>
        {[
          ["rental_terms_it", "Condizioni di noleggio — IT"],
          ["rental_terms_en", "Rental terms — EN"],
          ["privacy_it", "Privacy — IT"],
          ["privacy_en", "Privacy — EN"],
        ].map(([k, label]) => (
          <label key={k} className="block mt-2 text-[10px] font-bold text-gray-400 uppercase">
            {label}
            <textarea value={content[k] || ""} onChange={(e) => setContent({ ...content, [k]: e.target.value })} rows={4} className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs" data-testid={`admin-content-${k}`} />
          </label>
        ))}
        <button onClick={saveContent} className="mt-2 h-9 rounded-lg bg-fr-navy text-white text-xs font-bold px-4" data-testid="admin-content-save">Salva contenuti legali</button>
        <p className="text-[10px] text-gray-400 mt-1">Se vuoti, le pagine mostrano "In aggiornamento – contattaci".</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="admin-outbox">
        <p className="text-sm font-extrabold text-fr-navy">Registro invii email (modalità prova — nessun fornitore configurato [DA COMPLETARE])</p>
        {outbox.length === 0 && <p className="text-xs text-gray-400 mt-1">Nessun invio registrato.</p>}
        {outbox.slice(0, 10).map((o) => (
          <p key={o.id} className="text-[11px] text-gray-500 mt-1" data-testid={`admin-outbox-${o.kind}`}>[{o.status}] {o.kind}: {o.to} — {o.subject}</p>
        ))}
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="admin-audit">
        <p className="text-sm font-extrabold text-fr-navy">Registro attività (ultime 20)</p>
        {audit.slice(0, 20).map((a) => (
          <p key={a.id} className="text-[11px] text-gray-500 mt-1">{format(new Date(a.created_at), "d MMM HH:mm", { locale: it })} — {a.action} — {a.actor} {a.detail && <span className="text-gray-400">({a.detail})</span>}</p>
        ))}
      </div>
    </div>
  );
};

/* ---------- MULTE ---------- */
export const MulteTab = ({ token, api }) => {
  const [fines, setFines] = useState([]);
  const [nw, setNw] = useState({ date: "2026-10-21", time: "10:30", place: "", targa: "", amount: "", note: "" });
  const load = () => { api(token).get("/fines").then(({ data }) => setFines(data.fines)).catch(() => {}); };
  useEffect(() => { load(); }, [token]);

  const create = async () => {
    try { await api(token).post("/fines", { ...nw, amount: Number(nw.amount) }); toast.success("Verbale registrato: conferma tu l'attribuzione"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const assign = async (id, ref) => {
    try { await api(token).patch(`/fines/${id}`, { assigned_booking_reference: ref, driver_email: "" }); toast.success("Attribuzione confermata"); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="admin-tab-multe">
      <p className="text-xs text-gray-500 mb-3">Il sistema PROPONE contratto e conducente corrispondenti al periodo ma NON attribuisce responsabilità in automatico: la conferma è tua.</p>
      <div className="flex flex-wrap gap-2 bg-white border border-gray-200 rounded-xl p-3 mb-4 items-end" data-testid="admin-fines-new">
        <input type="date" value={nw.date} onChange={(e) => setNw({ ...nw, date: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <input type="time" value={nw.time} onChange={(e) => setNw({ ...nw, time: e.target.value })} className="h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <input value={nw.place} onChange={(e) => setNw({ ...nw, place: e.target.value })} placeholder="Luogo (ZTL via…)" className="h-9 w-40 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <input value={nw.targa} onChange={(e) => setNw({ ...nw, targa: e.target.value })} placeholder="Targa" className="h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs uppercase" />
        <input type="number" value={nw.amount} onChange={(e) => setNw({ ...nw, amount: e.target.value })} placeholder="Importo €" className="h-9 w-24 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" />
        <button onClick={create} className="h-9 rounded-lg bg-fr-navy text-white text-xs font-bold px-3" data-testid="admin-fine-create">Registra</button>
      </div>
      <div className="space-y-2" data-testid="admin-fines-list">
        {fines.map((f) => (
          <div key={f.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid={`admin-fine-${f.id}`}>
            <p className="text-xs font-bold text-fr-navy">{f.targa} — {f.date} {f.time} — €{f.amount} — {f.place} <span className={`text-[10px] rounded px-1.5 py-0.5 ${f.status === "assegnata" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{f.status}</span></p>
            <p className="text-[11px] text-gray-500 mt-1">Candidati (prenotazioni con questa targa nel periodo):</p>
            {f.suggestions.length === 0 && <p className="text-[11px] text-gray-400">Nessuna prenotazione trovata: [DA COMPLETARE: verifica manuale]</p>}
            <div className="mt-1 flex flex-wrap gap-1.5">
              {f.suggestions.map((sg) => (
                <button key={sg.reference} onClick={() => assign(f.id, sg.reference)} className="text-[10px] font-bold bg-gray-100 rounded px-2 py-1 hover:bg-gray-200" data-testid={`admin-fine-assign-${sg.reference}`}>
                  {sg.reference} — {sg.customer}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- D5: PANNELLO DI CONTROLLO (indicatori SOLO da dati reali, fuso Europe/Rome) ---------- */
export const DashboardTab = ({ token, api }) => {
  const [d, setD] = useState(null);
  const load = () => { api(token).get("/dashboard").then(({ data }) => setD(data)).catch(() => {}); };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [token]);
  if (!d) return <p className="text-sm text-gray-400">Dato non disponibile: carico i valori reali…</p>;
  const card = (label, value, testid, tone = "text-fr-navy") => (
    <div key={testid} className="bg-white border border-gray-200 rounded-xl p-4" data-testid={testid}>
      <p className="text-[10px] font-bold text-gray-400 uppercase">{label}</p>
      <p className={`text-2xl font-extrabold ${tone} mt-1`}>{value}</p>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="admin-tab-dashboard">
      <p className="text-xs text-gray-500">Indicatori calcolati SOLO dai dati reali (fuso Europe/Rome). Gli incassi contano solo pagamenti <b>incassati</b> — mai preautorizzazioni o cauzioni.</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {card("Veicoli totali", d.veicoli?.totali ?? 0, "dash-veicoli-totali")}
        {card("Disponibili", d.veicoli?.disponibili ?? 0, "dash-veicoli-disponibili", "text-green-700")}
        {card("Noleggiati", d.veicoli?.noleggiati ?? 0, "dash-veicoli-noleggiati")}
        {card("In manutenzione", d.veicoli?.in_manutenzione ?? 0, "dash-veicoli-manutenzione", "text-amber-700")}
        {card("Fuori flotta", d.veicoli?.fuori_flotta ?? 0, "dash-veicoli-fuoriflotta", "text-gray-500")}
        {card("Contratti attivi", d.contratti_attivi ?? 0, "dash-contratti")}
        {card("Incassi oggi", `€ ${(d.incassi_oggi ?? 0).toFixed(2)}`, "dash-incassi-oggi", "text-green-700")}
        {card("Incassi mese", `€ ${(d.incassi_mese ?? 0).toFixed(2)}`, "dash-incassi-mese", "text-green-700")}
        {card("Rientri prev. oggi", d.rientri_previsti_oggi ?? 0, "dash-rientri-oggi")}
        {card("Rientri in ritardo", d.rientri_in_ritardo ?? 0, "dash-rientri-ritardo", d.rientri_in_ritardo ? "text-fr-red" : "text-fr-navy")}
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="dash-urgenti">
        <p className="text-sm font-extrabold text-fr-navy">Elementi urgenti</p>
        <ul className="text-xs text-gray-600 mt-2 space-y-1">
          <li data-testid="dash-urgent-docs">Documenti da verificare: <b>{d.urgenti?.documenti_da_verificare ?? 0}</b></li>
          <li data-testid="dash-urgent-pays">Pagamenti incerti/da verificare: <b>{d.urgenti?.pagamenti_incerti ?? 0}</b></li>
          <li data-testid="dash-urgent-prereq">Consegne di oggi con prerequisiti mancanti: <b>{d.urgenti?.prerequisiti_mancanti ?? 0}</b></li>
        </ul>
      </div>
    </div>
  );
};

/* ---------- D2: CONSEGNE — elenco giornaliero con driver, stato, ritardi, prerequisiti ---------- */
export const ConsegneTab = ({ token, api }) => {
  const [items, setItems] = useState([]);
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const load = () => { api(token).get(`/deliveries?date=${day}`).then(({ data }) => setItems(data.deliveries || [])).catch(() => {}); };
  useEffect(() => { load(); }, [token, day]);

  const setDriver = async (id, email) => {
    try { await api(token).patch(`/deliveries/${id}`, { driver_email: email }); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api(token).patch(`/deliveries/${id}`, { status }); load(); } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const late = (it) => it.status !== "completata" && new Date(it.when) < new Date();
  return (
    <div data-testid="admin-tab-consegne">
      <div className="flex items-center gap-3 mb-3">
        <label className="text-[10px] font-bold text-gray-400 uppercase">Giorno
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="block h-9 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="admin-consegne-date" />
        </label>
        <span className="text-xs text-gray-400">{items.length} attività</span>
      </div>
      <div className="space-y-2" data-testid="admin-consegne-list">
        {items.length === 0 && <p className="text-sm text-gray-400">Nessuna consegna/ritiro programmata per il giorno scelto.</p>}
        {items.map((d) => (
          <div key={d.id} className={`bg-white border rounded-xl px-4 py-3 ${late(d) ? "border-red-300" : "border-gray-200"}`} data-testid={`admin-consegna-${d.booking_reference}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono font-extrabold text-fr-navy text-sm">{d.booking_reference}</span>
              <span className="text-[10px] font-bold rounded px-1.5 py-0.5 bg-gray-100 text-gray-600 uppercase">{d.tipo}</span>
              <span className="text-[11px] text-gray-500">{format(new Date(d.when), "HH:mm", { locale: it })} — {d.address || d.location_name}</span>
              {d.targa && <span className="text-[10px] font-mono text-gray-500">{d.targa}</span>}
              {late(d) && <span className="text-[10px] font-bold text-fr-red" data-testid="consegna-late">IN RITARDO</span>}
            </div>
            {d.prereq_missing?.length > 0 && (
              <p className="text-[10px] text-purple-800 font-bold mt-1" data-testid="consegna-prereq">Prerequisiti mancanti: {d.prereq_missing.join(", ")}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input defaultValue={d.driver_email || ""} onBlur={(e) => e.target.value !== (d.driver_email || "") && setDriver(d.id, e.target.value)} placeholder="email driver" className="h-8 w-44 rounded-lg border border-gray-200 bg-gray-50 px-2 text-[11px]" data-testid="consegna-driver" />
              {["da_fare", "in_viaggio", "completata"].map((s) => (
                <button key={s} onClick={() => setStatus(d.id, s)} className={`h-7 rounded-lg text-[10px] font-bold px-2 ${d.status === s ? "bg-fr-navy text-white" : "bg-gray-100 text-gray-600"}`}>{s.replace("_", " ")}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
